// src/components/Gallery/GalleryCompSwiper.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "primereact/button"; // اگر نمی‌خواهی، با دکمه ساده جایگزین کن
import { classNames } from "primereact/utils";

import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Thumbs,
  FreeMode,
  Autoplay,
  Keyboard,
  Zoom,
} from "swiper/modules";

import ImageAddress from "./ImageAddress.json";
import "./GalleriaAdvancedDemo.css"; // اگر استایل‌های دیگری داری نگه‌دار

export default function GalleryCompSwiper() {
  const { lang = "fa", id } = useParams();
  const galleryId = String(id);
  const isEN = lang === "en";

  // refs
  const mainSwiperRef = useRef(null);
  const thumbsSwiperRef = useRef(null);

  // states
  const [images, setImages] = useState([]);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [isAutoPlayActive, setAutoPlayActive] = useState(true);
  const [isFullScreen, setFullScreen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // زبان
  const groups = useMemo(() => {
    const fa = ImageAddress?.imageGroupsFA ?? [];
    const en = ImageAddress?.imageGroupsEN ?? [];
    return isEN ? en : fa;
  }, [isEN]);

  // بارگذاری آلبوم
  useEffect(() => {
    const gallery = groups.find((g) => String(g.imageId) === galleryId);

    if (!gallery) {
      setNotFound(true);
      setImages([]);
      setGalleryTitle("");
      return;
    }

    setNotFound(false);
    setGalleryTitle(gallery.title || "");
    const formatted = (gallery.images || []).map((src, i) => ({
      src,
      alt: gallery.imageText?.[i] || "",
      title: gallery.imageText?.[i] || "",
    }));
    setImages(formatted);
    setActiveIndex(0);
    window.scrollTo(0, 0);
  }, [groups, galleryId]);

  // Fullscreen handlers
  useEffect(() => {
    const onFsChange = () => setFullScreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("mozfullscreenchange", onFsChange);
    document.addEventListener("MSFullscreenChange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("mozfullscreenchange", onFsChange);
      document.removeEventListener("MSFullscreenChange", onFsChange);
    };
  }, []);

  const openFullScreen = () => {
    const elem = document.querySelector(".custom-galleria"); // همان کلاس قبلی
    if (!elem) return;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
  };
  const closeFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    else if (document.msExitFullscreen) document.msExitFullscreen();
  };
  const toggleFullScreen = () => (isFullScreen ? closeFullScreen() : openFullScreen());

  // autoplay toggle
  const toggleAutoPlay = () => {
    const s = mainSwiperRef.current;
    if (!s) return;
    if (isAutoPlayActive) {
      s.autoplay?.stop();
      setAutoPlayActive(false);
    } else {
      s.autoplay?.start();
      setAutoPlayActive(true);
    }
  };

  // آیکن‌ها مثل قبل
  const autoPlayIcon = classNames("pi", {
    "pi-play": !isAutoPlayActive,
    "pi-pause": isAutoPlayActive,
  });
  const fullScreenIcon = classNames("pi", {
    "pi-window-maximize": !isFullScreen,
    "pi-window-minimize": isFullScreen,
  });

  const galleriaClassName = classNames("custom-galleria", {
    fullscreen: isFullScreen,
  });

  // ریسپانسیو برای thumbnails
  const thumbBreakpoints = {
    560: { slidesPerView: 3, spaceBetween: 8 },
    768: { slidesPerView: 4, spaceBetween: 10 },
    960: { slidesPerView: 5, spaceBetween: 10 },
    1024: { slidesPerView: 6, spaceBetween: 12 },
  };

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>{isEN ? "Album Not Found" : "آلبوم پیدا نشد"} | Aramis</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="container py-5 text-center">
          {isEN ? "Album not found." : "آلبوم مورد نظر پیدا نشد."}
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isEN
            ? "Aramis Smart Industrial Automation"
            : "اتوماسیون صنعتی هوشمند صنایع آرامیس"}{" "}
          | {galleryTitle || (isEN ? "Gallery" : "گالری")}
        </title>
        <meta
          name="description"
          content={`${
            isEN
              ? "Aramis Smart Industrial Automation"
              : "اتوماسیون صنعتی هوشمند صنایع آرامیس"
          } | ${galleryTitle || (isEN ? "Gallery" : "گالری")}`}
        />
      </Helmet>

      <div className="container">
        <div className="card galleria-demo mb-5">
          <h2 className="text-center text-blue1 mb-2 mt-5 fs-5">
            {galleryTitle}
          </h2>

          {images.length > 0 ? (
            <div className={galleriaClassName}>
              {/* اسلایدر اصلی */}
              <Swiper
                modules={[Navigation, Pagination, Thumbs, Autoplay, Keyboard, Zoom]}
                onSwiper={(s) => {
                  // نگهداری اینستنس برای کنترل autoplay
                  mainSwiperRef.current = s;
                }}
                onSlideChange={(s) => setActiveIndex(s.activeIndex)}
                navigation
                pagination={{ clickable: true }}
                keyboard={{ enabled: true }}
                zoom={{ maxRatio: 2 }}
                loop={true}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                style={{ width: "100%" }}
                className="main-swiper"
                dir={isEN ? "ltr" : "rtl"} // اگر RTL می‌خواهی
                slidesPerView={1}
                spaceBetween={10}
              >
                {images.map((img, i) => (
                  <SwiperSlide key={i}>
                    <div className="swiper-zoom-container">
                      <img
                        src={img.src}
                        alt={img.alt}
                        loading="lazy"
                        style={{ width: "100%", height: "auto", objectFit: "contain", display: "block" }}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* نوار ابزار پایین (مثل Footer قبلی) */}
              <div className="custom-galleria-footer">
                <Button
                  icon="pi pi-list"
                  onClick={() => setShowThumbnails((s) => !s)}
                />
                <Button icon={autoPlayIcon} onClick={toggleAutoPlay} />

                {!!images?.length && (
                  <span className="title-container">
                    <span>
                      {activeIndex + 1}/{images.length}
                    </span>
                    <span className="title">
                      {images[activeIndex]?.title || ""}
                    </span>
                  </span>
                )}

                <Button
                  icon={fullScreenIcon}
                  onClick={toggleFullScreen}
                  className="fullscreen-button"
                />
              </div>

              {/* Thumbnails */}
              {showThumbnails && (
                <Swiper
                  modules={[FreeMode, Thumbs]}
                  onSwiper={(s) => (thumbsSwiperRef.current = s)}
                  freeMode={true}
                  watchSlidesProgress={true}
                  className="thumbs-swiper"
                  breakpoints={thumbBreakpoints}
                  slidesPerView={4}
                  spaceBetween={8}
                  dir={isEN ? "ltr" : "rtl"}
                >
                  {images.map((img, i) => (
                    <SwiperSlide key={`thumb-${i}`}>
                      <div
                        className="thumb-wrapper"
                        onClick={() => {
                          mainSwiperRef.current?.slideToLoop(i, 0); // همگام با loop
                          setActiveIndex(i);
                        }}
                      >
                        <img
                          src={img.src}
                          alt={img.alt}
                          style={{ display: "block", width: "100%", height: "auto" }}
                          loading="lazy"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          ) : (
            <div className="py-5 text-center">
              {isEN ? "Loading…" : "در حال بارگذاری…"}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
